import { useMutation } from '@tanstack/react-query';
import { featureTypeId } from '@/enums';
import { useRequiredAppSelector } from '@/hooks';
import { useAttachEntityTag } from './useAttachEntityTag';
import { useCreateFeature } from './useFeatureMutations';

type EntityType = 'act' | 'fragment' | 'scene';

interface CreateLocationAndAttachData {
    name: string;
    description?: string;
    sublocationName?: string;
    entityType: EntityType;
    entityId: number;
    removeTags?: number[];
}

export function useCreateLocationAndAttach() {
    const projectId = useRequiredAppSelector('projectId');
    const createFeature = useCreateFeature();
    const attachTag = useAttachEntityTag();

    return useMutation({
        mutationFn: async ({ name, description, sublocationName, entityType, entityId, removeTags = [] }: CreateLocationAndAttachData) => {
            // Create the parent location
            const result = await createFeature.mutateAsync({
                project_id: projectId,
                data: {
                    name,
                    description: description || '',
                    feature_type_id: featureTypeId.location,
                },
            });

            const tagIds = [result.data.entity_tag.id];

            // Optionally create sublocation
            if (sublocationName) {
                const subResult = await createFeature.mutateAsync({
                    project_id: projectId,
                    data: {
                        name: sublocationName,
                        feature_type_id: featureTypeId.location,
                        parent_id: result.data.id,
                    },
                });
                tagIds.push(subResult.data.entity_tag.id);
            }

            // Attach all tag IDs (and remove any existing location tags for single-select)
            await attachTag.mutateAsync({
                entityType,
                entityId,
                addTags: tagIds,
                removeTags,
            });

            return result;
        },

        onError: error => {
            // biome-ignore lint/suspicious/noConsole: surfaces mutation failures during development
            console.error('Failed to create location and attach:', error);
        },
    });
}
